import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Linkedin, Building2, X, Mail, ChevronDown, Users, Check, Phone, LogOut, LogIn, Filter, Globe, QrCode, Ticket, User } from 'lucide-react';
import QRCode from 'qrcode.react';

const API_BASE  = '/api';
const AUTH_BASE = '/auth';

const SUPPORTED_LANGS = ['fr', 'en', 'de'];
const LANG_LABELS     = { fr: 'FR', en: 'EN', de: 'DE' };

const I18N = {
  fr: {
    networking:            'Networking',
    search:                'Rechercher…',
    tab_participants:      'Participants',
    tab_connected:         'Connectés',
    connect:               'Se connecter',
    pending:               'En attente',
    connected:             'Connecté·e',
    accept:                'Accepter',
    decline:               'Refuser',
    login_title:           'Identification',
    login_subtitle:        'Entrez votre email pour vous connecter',
    email_label:           'Adresse email',
    email_placeholder:     'votre@email.com',
    send_code:             'Envoyer le code',
    code_label:            'Code de vérification',
    code_hint:             'Code à 6 chiffres reçu par email',
    verify:                'Vérifier',
    not_found:             "Compte non trouvé. Contactez l'organisateur.",
    cancel:                'Annuler',
    back:                  'Retour',
    no_participants:       'Aucun participant',
    no_results:            'Aucun résultat',
    no_connected:          'Aucune connexion établie',
    sending:               'Envoi…',
    verifying:             'Vérification…',
    pending_received_hint: 'Demande reçue',
    show_contact:          'Voir les coordonnées',
    login_btn:             'S\'identifier',
    logout:                'Déconnexion',
    locked_title:          'Bientôt disponible',
    locked_message:        'Le networking sera bientôt ouvert.',
    filter_activities:     'Activités',
    filter_categories:     'Catégories',
    filter_requests:       'Recherche',
    filter_all:            'Effacer',
    filter_company_type:   'Type',
    filter_all_types:      'Tous les types',
    sort_by:               'Trier',
    sort_default:          'Trier par pertinence',
    sort_lastname:         'Trier par nom',
    sort_firstname:        'Trier par prénom',
    sort_company:          'Trier par société',
    sort_type:             'Trier par type',
    filters:               'Filtres',
    table_label:           'Table',
    common_points:         'points communs',
    view_profile:          'Voir le profil',
    tab_badge:             'Mon QrCode',
    badge_subtitle:        'Présentez ce QrCode pour vous connecter à un autre participant',
    badge_add_wallet:      'Ajouter à Apple Wallet',
    badge_locked_title:    'Bientôt disponible',
    badge_locked_message:  'Le QrCode sera bientôt accessible.',
    rate_limit_title:      'Pause networking',
    rate_limit_msg:        'Vous avez atteint la limite de 10 demandes de connexion sur 10 minutes. Veuillez patienter avant d\'en envoyer de nouvelles.',
    rate_limit_hint:       'Vous pourrez reprendre dans :',
    rate_limit_ok:         'Compris',
  },
  en: {
    networking:            'Networking',
    search:                'Search…',
    tab_participants:      'Participants',
    tab_connected:         'Connected',
    connect:               'Connect',
    pending:               'Pending',
    connected:             'Connected',
    accept:                'Accept',
    decline:               'Decline',
    login_title:           'Sign in',
    login_subtitle:        'Enter your email to connect',
    email_label:           'Email address',
    email_placeholder:     'your@email.com',
    send_code:             'Send code',
    code_label:            'Verification code',
    code_hint:             '6-digit code sent to your email',
    verify:                'Verify',
    not_found:             'Account not found. Contact the organizer.',
    cancel:                'Cancel',
    back:                  'Back',
    no_participants:       'No participants',
    no_results:            'No results',
    no_connected:          'No connections yet',
    sending:               'Sending…',
    verifying:             'Verifying…',
    pending_received_hint: 'Request received',
    show_contact:          'View contact details',
    login_btn:             'Sign in',
    logout:                'Sign out',
    locked_title:          'Coming soon',
    locked_message:        'Networking will be available soon.',
    filter_activities:     'Activities',
    filter_categories:     'Categories',
    filter_requests:       'Looking for',
    filter_all:            'Clear',
    filter_company_type:   'Type',
    filter_all_types:      'All types',
    sort_by:               'Sort',
    sort_default:          'Relevance',
    sort_lastname:         'Last name',
    sort_firstname:        'First name',
    sort_company:          'Company',
    sort_type:             'Type',
    filters:               'Filters',
    table_label:           'Table',
    common_points:         'common points',
    view_profile:          'View profile',
    tab_badge:             'My QrCode',
    badge_subtitle:        'Show this QrCode to connect with another participant',
    badge_add_wallet:      'Add to Apple Wallet',
    badge_locked_title:    'Coming soon',
    badge_locked_message:  'The QrCode will be available soon.',
    rate_limit_title:      'Networking pause',
    rate_limit_msg:        'You have reached the limit of 10 connection requests in 10 minutes. Please wait before sending new ones.',
    rate_limit_hint:       'You can resume in:',
    rate_limit_ok:         'Got it',
  },
  de: {
    networking:            'Networking',
    search:                'Suchen…',
    tab_participants:      'Teilnehmer',
    tab_connected:         'Verbunden',
    connect:               'Verbinden',
    pending:               'Ausstehend',
    connected:             'Verbunden',
    accept:                'Annehmen',
    decline:               'Ablehnen',
    login_title:           'Anmeldung',
    login_subtitle:        'E-Mail eingeben um fortzufahren',
    email_label:           'E-Mail-Adresse',
    email_placeholder:     'ihre@email.com',
    send_code:             'Code senden',
    code_label:            'Bestätigungscode',
    code_hint:             '6-stelliger Code per E-Mail',
    verify:                'Bestätigen',
    not_found:             'Konto nicht gefunden. Veranstalter kontaktieren.',
    cancel:                'Abbrechen',
    back:                  'Zurück',
    no_participants:       'Keine Teilnehmer',
    no_results:            'Keine Ergebnisse',
    no_connected:          'Noch keine Verbindungen',
    sending:               'Senden…',
    verifying:             'Überprüfen…',
    pending_received_hint: 'Anfrage erhalten',
    show_contact:          'Kontaktdaten anzeigen',
    login_btn:             'Anmelden',
    logout:                'Abmelden',
    locked_title:          'Demnächst verfügbar',
    locked_message:        'Das Networking wird bald verfügbar sein.',
    filter_activities:     'Aktivitäten',
    filter_categories:     'Kategorien',
    filter_requests:       'Gesucht',
    filter_all:            'Löschen',
    filter_company_type:   'Typ',
    filter_all_types:      'Alle Typen',
    sort_by:               'Sortieren',
    sort_default:          'Relevanz',
    sort_lastname:         'Nachname',
    sort_firstname:        'Vorname',
    sort_company:          'Unternehmen',
    sort_type:             'Typ',
    filters:               'Filter',
    table_label:           'Tisch',
    common_points:         'gemeinsame Punkte',
    view_profile:          'Profil ansehen',
    tab_badge:             'Mein QrCode',
    badge_subtitle:        'Zeigen Sie diesen QrCode, um sich mit einem anderen Teilnehmer zu verbinden',
    badge_add_wallet:      'Zu Apple Wallet hinzufügen',
    badge_locked_title:    'Demnächst verfügbar',
    badge_locked_message:  'Der QrCode wird bald verfügbar sein.',
    rate_limit_title:      'Networking-Pause',
    rate_limit_msg:        'Sie haben das Limit von 10 Verbindungsanfragen in 10 Minuten erreicht. Bitte warten Sie.',
    rate_limit_hint:       'Sie können weitermachen in:',
    rate_limit_ok:         'Verstanden',
  },
};

// ── Rate limiting networking (10 demandes / 10 minutes) ──────────────────────
const NET_RL_MAX    = 10;
const NET_RL_WIN_MS = 10 * 60 * 1000;

function netCheckRateLimit(senderId) {
  try {
    const raw = localStorage.getItem(`net_rl_${senderId}`);
    if (!raw) return { limited: false, retryAfter: 0 };
    const d = JSON.parse(raw);
    const elapsed = Date.now() - d.windowStart;
    if (elapsed >= NET_RL_WIN_MS) return { limited: false, retryAfter: 0 };
    if (d.count >= NET_RL_MAX) return { limited: true, retryAfter: Math.ceil((NET_RL_WIN_MS - elapsed) / 1000) };
    return { limited: false, retryAfter: 0 };
  } catch { return { limited: false, retryAfter: 0 }; }
}

function netIncrementRateLimit(senderId) {
  try {
    const raw = localStorage.getItem(`net_rl_${senderId}`);
    const now = Date.now();
    let d = raw ? JSON.parse(raw) : null;
    if (!d || (now - d.windowStart) >= NET_RL_WIN_MS) {
      d = { count: 1, windowStart: now };
    } else {
      d = { ...d, count: d.count + 1 };
    }
    localStorage.setItem(`net_rl_${senderId}`, JSON.stringify(d));
  } catch {}
}

const getInitialLang = () => {
  const saved = localStorage.getItem('language');
  if (SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browser) ? browser : 'fr';
};

const MODAL_INIT = { open: false, step: 'email', email: '', code: '', loading: false, error: '', pendingConnectId: null };

const CACHE_TTL = 3 * 60 * 1000; // 3 minutes

const readCache = (slug) => {
  try {
    const raw = sessionStorage.getItem(`net_cache_${slug}`);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL) return null;
    return entry;
  } catch { return null; }
};

const writeCache = (slug, data) => {
  try {
    sessionStorage.setItem(`net_cache_${slug}`, JSON.stringify({ ...data, ts: Date.now() }));
  } catch {}
};

const NetworkingParticipantsPage = () => {
  const { slug } = useParams();
  const navigate  = useNavigate();

  const [lang, setLang]                   = useState(getInitialLang);
  const [eventData, setEventData]         = useState(null);
  const [participants, setParticipants]   = useState([]);
  const [connections, setConnections]     = useState([]);
  const [networkingUser, setNetworkingUser] = useState(null); // { email, record_id }
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [search, setSearch]               = useState('');
  const [activeTab, setActiveTab]         = useState('participants');
  const [actionLoading, setActionLoading] = useState({});
  const [rateLimitModal, setRateLimitModal]       = useState({ open: false, retryAfter: 0 });
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [loginModal, setLoginModal]       = useState(MODAL_INIT);
  const [networkingConfig, setNetworkingConfig] = useState(null);
  const [qrcodeConfig, setQrcodeConfig]         = useState(null);
  const [selectedCategories, setSelectedCategories]   = useState([]);
  const [selectedRequests, setSelectedRequests]       = useState([]);
  const [selectedCompanyType, setSelectedCompanyType] = useState('');
  const [sortField, setSortField]                     = useState('default');
  const [filtersOpen, setFiltersOpen]                 = useState(false);

  const subEventIdRef = useRef(null);
  const i = I18N[lang];

  // ── Current user's activities and requests (from participants list) ──────────
  const myProfile = useMemo(() => {
    if (!networkingUser?.record_id || !participants.length) return null;
    return participants.find(p => p.id === networkingUser.record_id) || null;
  }, [networkingUser, participants]);

  // ── All unique categories and requests (for filter chips) ────────────────────
  const allCategories = useMemo(() => {
    const set = new Set();
    participants.forEach(p => (p.category || []).forEach(c => c && set.add(c)));
    return Array.from(set).sort();
  }, [participants]);

  const allRequests = useMemo(() => {
    const set = new Set();
    participants.forEach(p => (p.company_requests || []).forEach(r => r && set.add(r)));
    return Array.from(set).sort();
  }, [participants]);

  const allCompanyTypes = useMemo(() => {
    const set = new Set();
    participants.forEach(p => p.company_type && set.add(p.company_type));
    return Array.from(set).sort();
  }, [participants]);

  // ── Initial load (single bundle request) ──────────────────────────────────
  useEffect(() => {
    const load = async () => {
      // Use local cache if fresh — instant display, no spinner
      const cache = readCache(slug);
      if (cache) {
        setEventData(cache.eventData);
        setParticipants(cache.participants);
        if (cache.networkingConfig) setNetworkingConfig(cache.networkingConfig);
        if (cache.qrcodeConfig) setQrcodeConfig(cache.qrcodeConfig);
        subEventIdRef.current = cache.subEventId;
        localStorage.setItem(`subevent_id_${slug}`, cache.subEventId);
        setLoading(false);
        await checkExistingAuth(cache.subEventId);
        return;
      }

      try {
        // Single bundle call: event + participants + content-display
        // If we have a cached subEventId, use it (cache hit on server); otherwise pass slug to resolve
        const storedSubEventId = localStorage.getItem(`subevent_id_${slug}`);
        const bundleId = storedSubEventId || 'by-slug';
        const bundleRes = await fetch(`${API_BASE}/networking-bundle/${bundleId}?slug=${encodeURIComponent(slug)}`);
        const bundle    = await bundleRes.json();
        if (!bundle.success || !bundle.event) throw new Error('Événement introuvable');

        const ev         = bundle.event;
        const subEventId = ev.subevent_id || ev.id;
        setEventData(ev);
        subEventIdRef.current = subEventId;
        localStorage.setItem(`subevent_id_${slug}`, subEventId);

        if (bundle.participants) setParticipants(bundle.participants);

        let cfg = null;
        let qrCfg = null;
        if (bundle.content_display) {
          cfg = bundle.content_display.find(c => c.type_id === 'Networking') || null;
          qrCfg = bundle.content_display.find(c => c.type_id === 'QrCode') || null;
          setNetworkingConfig(cfg);
          setQrcodeConfig(qrCfg);
        }

        writeCache(slug, {
          eventData: ev,
          subEventId,
          participants: bundle.participants || [],
          networkingConfig: cfg,
          qrcodeConfig: qrCfg,
        });

        await checkExistingAuth(subEventId);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // ── Load connections + re-fetch participants with contact info when user identified ──
  useEffect(() => {
    if (!networkingUser?.record_id || !subEventIdRef.current) return;
    const myId       = networkingUser.record_id;
    const subEventId = subEventIdRef.current;
    // Single bundle call with user ID — returns connections + participants with PII
    fetch(`${API_BASE}/networking-bundle/${subEventId}?me=${myId}`)
      .then(r => r.json())
      .then(bundle => {
        if (bundle.connections) setConnections(bundle.connections);
        if (bundle.participants) setParticipants(bundle.participants);
      })
      .catch(() => {});
  }, [networkingUser]);

  // ── Auth detection ────────────────────────────────────────────────────────
  const checkExistingAuth = async (subEventId) => {
    // 1. Networking session (localStorage)
    try {
      const net = localStorage.getItem('networking_user');
      if (net) {
        const parsed = JSON.parse(net);
        if (parsed.record_id && parsed.email) { setNetworkingUser(parsed); return; }
      }
    } catch {}

    // 2. Session dashboard principale (localStorage)
    try {
      const token   = localStorage.getItem('user_token');
      const raw     = localStorage.getItem('user_data');
      const expires = localStorage.getItem('session_expires');
      if (token && raw && expires && new Date(expires) > new Date()) {
        const parsed = JSON.parse(raw);
        if (parsed.email) {
          const res  = await fetch(`${API_BASE}/profile?email=${encodeURIComponent(parsed.email)}&subevent=${subEventId}`);
          const data = await res.json();
          if (data.success && data.profile?.record_id) {
            const u = { email: parsed.email, record_id: data.profile.record_id };
            setNetworkingUser(u);
            localStorage.setItem('networking_user', JSON.stringify(u));
          }
        }
      }
    } catch {}
  };

  // ── Connection state helper ───────────────────────────────────────────────
  const getConnectionState = (participantId) => {
    if (!networkingUser) return null;
    const conn = connections.find(c =>
      (c.sender_id === networkingUser.record_id && c.receiver_id === participantId) ||
      (c.receiver_id === networkingUser.record_id && c.sender_id === participantId)
    );
    if (!conn) return null;
    const type =
      conn.status === 'on'      ? 'connected' :
      conn.status === 'decline' ? 'declined'  :
      conn.sender_id === networkingUser.record_id ? 'pending_sent' : 'pending_received';
    return { type, connectionId: conn.id };
  };

  // ── Rate limit countdown ─────────────────────────────────────────────────
  useEffect(() => {
    if (!rateLimitModal.open) return;
    setRateLimitCountdown(rateLimitModal.retryAfter);
    const iv = setInterval(() => {
      setRateLimitCountdown(prev => {
        if (prev <= 1) { clearInterval(iv); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [rateLimitModal.open, rateLimitModal.retryAfter]);

  // ── Connect action ────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('networking_user');
    setNetworkingUser(null);
    setConnections([]);
    setActiveTab('participants');
  };

  const doConnect = async (senderId, receiverId) => {
    // Check localStorage avant l'appel réseau
    const rl = netCheckRateLimit(senderId);
    if (rl.limited) { setRateLimitModal({ open: true, retryAfter: rl.retryAfter }); return; }
    setActionLoading(prev => ({ ...prev, [receiverId]: 'connect' }));
    try {
      const res  = await fetch(`${API_BASE}/networking-connect`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ sender_id: senderId, receiver_id: receiverId, subevent_id: subEventIdRef.current }),
      });
      const data = await res.json();
      if (data.rate_limited) {
        setRateLimitModal({ open: true, retryAfter: data.retry_after || 600 });
      } else if (data.success) {
        if (!data.already_exists) netIncrementRateLimit(senderId);
        setConnections(prev => [
          ...prev.filter(c => !(c.sender_id === senderId && c.receiver_id === receiverId)),
          { id: data.connection_id, status: 'Pending', sender_id: senderId, receiver_id: receiverId },
        ]);
      }
    } catch {}
    setActionLoading(prev => ({ ...prev, [receiverId]: null }));
  };

  const handleConnect = (participantId) => {
    if (isEventAccessClosed) return;
    if (!networkingUser) {
      setLoginModal({ ...MODAL_INIT, open: true, pendingConnectId: participantId });
      return;
    }
    doConnect(networkingUser.record_id, participantId);
  };

  // ── Respond action ────────────────────────────────────────────────────────
  const handleRespond = async (connectionId, status, participantId) => {
    if (isEventAccessClosed) return;
    setActionLoading(prev => ({ ...prev, [participantId]: status }));
    try {
      const res  = await fetch(`${API_BASE}/networking-respond/${connectionId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        setConnections(prev => prev.map(c => c.id === connectionId ? { ...c, status } : c));
        if (status === 'on') setActiveTab('connected');
      }
    } catch {}
    setActionLoading(prev => ({ ...prev, [participantId]: null }));
  };

  // ── Login modal — email step ──────────────────────────────────────────────
  const handleLoginEmail = async () => {
    if (!loginModal.email) return;
    setLoginModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res  = await fetch(`${AUTH_BASE}/send_networking_code.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          email:      loginModal.email,
          event_id:   subEventIdRef.current,
          event_name: eventData?.name || '',
          color_main: eventData?.color_main || '#1c1b3a',
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setLoginModal(prev => ({ ...prev, loading: false, step: 'code' }));
      } else {
        setLoginModal(prev => ({ ...prev, loading: false, error: data.not_found ? i.not_found : (data.error || i.not_found) }));
      }
    } catch {
      setLoginModal(prev => ({ ...prev, loading: false, error: i.not_found }));
    }
  };

  // ── Login modal — code step ───────────────────────────────────────────────
  const handleLoginCode = async () => {
    if (loginModal.code.length !== 6) return;
    setLoginModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const verRes  = await fetch(`${AUTH_BASE}/verify_code.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: loginModal.email, event_id: subEventIdRef.current, code: loginModal.code }),
      });
      const verData = await verRes.json();
      if (!verData.ok) {
        setLoginModal(prev => ({ ...prev, loading: false, error: verData.error || 'Code invalide' }));
        return;
      }

      const profRes  = await fetch(`${API_BASE}/profile?email=${encodeURIComponent(loginModal.email)}&subevent=${subEventIdRef.current}`);
      const profData = await profRes.json();
      if (!profData.success || !profData.profile?.record_id) {
        setLoginModal(prev => ({ ...prev, loading: false, error: i.not_found }));
        return;
      }

      const u = { email: loginModal.email, record_id: profData.profile.record_id };
      setNetworkingUser(u);
      localStorage.setItem('networking_user', JSON.stringify(u));

      const pendingId = loginModal.pendingConnectId;
      setLoginModal(MODAL_INIT);

      if (pendingId) doConnect(u.record_id, pendingId);
    } catch {
      setLoginModal(prev => ({ ...prev, loading: false, error: 'Erreur de vérification' }));
    }
  };

  // ── Common points score ───────────────────────────────────────────────────
  const commonScore = (p) => {
    if (!myProfile) return 0;
    const myCats = new Set(myProfile.category || []);
    const myReqs = new Set(myProfile.company_requests || []);
    let score = 0;
    (p.category || []).forEach(c => { if (myCats.has(c)) score++; });
    (p.company_requests || []).forEach(r => { if (myReqs.has(r)) score++; });
    return score;
  };

  // ── Filter + sort logic ───────────────────────────────────────────────────
  const visible = participants.filter(p => p.id !== networkingUser?.record_id);

  const matchesSearch = (p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.lastname + ' ' + p.firstname).toLowerCase().includes(q) ||
      p.company?.toLowerCase().includes(q) ||
      p.fonction?.toLowerCase().includes(q) ||
      p.category?.some(c => c.toLowerCase().includes(q))
    );
  };

  const matchesFilters = (p) => {
    if (selectedCompanyType && p.company_type !== selectedCompanyType) return false;
    if (selectedCategories.length > 0) {
      const hasAct = selectedCategories.some(c => (p.category || []).includes(c));
      if (!hasAct) return false;
    }
    if (selectedRequests.length > 0) {
      const hasReq = selectedRequests.some(r => (p.company_requests || []).includes(r));
      if (!hasReq) return false;
    }
    return true;
  };

  const priorityOf = (p) => {
    const s = getConnectionState(p.id);
    if (s?.type === 'pending_received') return 0;
    if (s?.type === 'pending_sent')     return 1;
    if (s?.type === 'declined')         return 4;
    if (s?.type === 'connected')        return 3;
    return 2; // no connection — sorted by common score below
  };

  const sortedParticipants = visible
    .filter(p => matchesSearch(p) && matchesFilters(p))
    .sort((a, b) => {
      if (sortField !== 'default') {
        switch (sortField) {
          case 'lastname':  return (a.lastname  || '').localeCompare(b.lastname  || '');
          case 'firstname': return (a.firstname || '').localeCompare(b.firstname || '');
          case 'company': {
            const ac = a.company || '', bc = b.company || '';
            if (!ac && bc) return 1;
            if (ac && !bc) return -1;
            return ac.localeCompare(bc);
          }
          case 'type':      return (a.company_type || '').localeCompare(b.company_type || '');
          default: break;
        }
      }
      const pa = priorityOf(a), pb = priorityOf(b);
      if (pa !== pb) return pa - pb;
      if (pa === 2) {
        const sa = commonScore(a), sb = commonScore(b);
        if (sa !== sb) return sb - sa;
      }
      return (a.lastname + a.firstname).localeCompare(b.lastname + b.firstname);
    });

  const connectedList = visible.filter(p =>
    getConnectionState(p.id)?.type === 'connected' && matchesSearch(p) && matchesFilters(p)
  );

  // ── Filter chip toggle ────────────────────────────────────────────────────
  const toggleCategory = (c) => {
    setSelectedCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };
  const toggleRequest = (r) => {
    setSelectedRequests(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };
  const clearFilters = () => { setSelectedCategories([]); setSelectedRequests([]); setSelectedCompanyType(''); };
  const hasFilters = selectedCategories.length > 0 || selectedRequests.length > 0 || !!selectedCompanyType;
  const activeFilterCount = selectedCategories.length + selectedRequests.length + (selectedCompanyType ? 1 : 0);

  // ── Connect button ────────────────────────────────────────────────────────
  const renderButton = (p) => {
    const state   = getConnectionState(p.id);
    const busy    = actionLoading[p.id];
    const disabled = isEventAccessClosed;

    if (!networkingUser) {
      return (
        <button onClick={() => handleConnect(p.id)} disabled={disabled}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition flex-shrink-0 ${disabled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
          {i.connect}
        </button>
      );
    }

    if (!state) {
      return (
        <button onClick={() => handleConnect(p.id)} disabled={!!busy || disabled}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition disabled:opacity-50 flex-shrink-0 ${disabled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
          {busy ? '…' : i.connect}
        </button>
      );
    }

    if (state.type === 'pending_received') {
      return (
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => handleRespond(state.connectionId, 'on', p.id)} disabled={!!busy || disabled}
            className={`text-xs font-semibold px-2.5 py-1.5 rounded-full transition disabled:opacity-50 ${disabled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}>
            {busy === 'on' ? '…' : i.accept}
          </button>
          <button onClick={() => handleRespond(state.connectionId, 'decline', p.id)} disabled={!!busy || disabled}
            className={`text-xs font-semibold px-2.5 py-1.5 rounded-full transition disabled:opacity-50 ${disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}>
            {busy === 'decline' ? '…' : i.decline}
          </button>
        </div>
      );
    }

    if (state.type === 'pending_sent') {
      return <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-orange-100 text-orange-600 flex-shrink-0">{i.pending}</span>;
    }

    if (state.type === 'connected') {
      return <span className="text-xs font-semibold px-3 py-1.5 rounded-full border border-green-500 text-green-600 flex-shrink-0">{i.connected}</span>;
    }

    if (state.type === 'declined') {
      return <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-400 flex-shrink-0">{i.decline}</span>;
    }

    return null;
  };

  // ── Participant card ──────────────────────────────────────────────────────
  const renderCard = (p) => {
    const state             = getConnectionState(p.id);
    const isPendingReceived = state?.type === 'pending_received';
    const isConnected       = state?.type === 'connected';
    const hasContact        = isConnected && (p.email || p.phone || p.linkedin);
    const score             = commonScore(p);

    return (
      <div key={p.id} className={`bg-white rounded-xl shadow-sm border p-4 flex gap-3 ${isPendingReceived ? 'border-blue-200 bg-blue-50/30' : isConnected ? 'border-green-100' : 'border-gray-100'}`}>
        {/* Avatar — clickable to profile */}
        <button
          onClick={() => navigate(`/event/${slug}/participants-networking/${p.id}`)}
          className="flex-shrink-0 focus:outline-none"
          title={i.view_profile}
        >
          {p.photo || p.photo_thumb ? (
            <img src={p.photo || p.photo_thumb} alt={p.firstname} className="w-12 h-12 rounded-full object-cover hover:ring-2 hover:ring-blue-400 transition" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center hover:ring-2 hover:ring-blue-400 transition">
              <span className="text-blue-600 font-semibold text-base">{(p.lastname?.[0] || '') + (p.firstname?.[0] || '')}</span>
            </div>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {/* Lastname first */}
              <button
                onClick={() => navigate(`/event/${slug}/participants-networking/${p.id}`)}
                className="text-left focus:outline-none"
              >
                <p className="font-semibold text-gray-900 text-sm leading-tight hover:text-blue-600 transition">
                  <span className="uppercase">{p.lastname}</span>{' '}
                  <span>{p.firstname}</span>
                </p>
              </button>
              {p.fonction && <p className="text-xs text-gray-500 truncate mt-0.5">{p.fonction}</p>}
              {p.company && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Building2 size={10} className="text-gray-400 flex-shrink-0" />
                  <p className="text-xs text-gray-600 truncate">{p.company}</p>
                </div>
              )}
              {p.company_type && (
                <span className="inline-block text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium mt-0.5">{p.company_type}</span>
              )}
              {p.table && (
                <p className="text-[10px] text-gray-400 mt-0.5">{i.table_label} : {p.table}</p>
              )}
            </div>
            <div className="flex-shrink-0">
              {renderButton(p)}
            </div>
          </div>

          {/* Categories */}
          {p.category?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {p.category.slice(0, 3).map((c, idx) => {
                const isCommon = myProfile && (myProfile.category || []).includes(c);
                return (
                  <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${isCommon ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>
                    {c}
                  </span>
                );
              })}
              {p.category.length > 3 && (
                <span className="text-xs text-gray-400 px-1 py-0.5">+{p.category.length - 3}</span>
              )}
            </div>
          )}

          {/* Company requests */}
          {p.company_requests?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {p.company_requests.slice(0, 2).map((r, idx) => {
                const isCommon = myProfile && (myProfile.company_requests || []).includes(r);
                return (
                  <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${isCommon ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700'}`}>
                    {r}
                  </span>
                );
              })}
            </div>
          )}

          {/* Common score badge (only when logged in and score > 0) */}
          {networkingUser && score > 0 && !isConnected && (
            <p className="text-[10px] text-green-600 font-medium mt-1">
              ✓ {score} {i.common_points}
            </p>
          )}

          {isPendingReceived && (
            <p className="text-xs text-blue-600 font-medium mt-1.5">↑ {i.pending_received_hint}</p>
          )}

          {hasContact && (
            <details className="mt-2 pt-2 border-t border-gray-100">
              <summary className="text-xs text-green-600 font-medium cursor-pointer select-none list-none hover:text-green-700">
                ▶ {i.show_contact}
              </summary>
              <div className="mt-2 space-y-1.5">
                {p.email && (
                  <a href={`mailto:${p.email}`} className="flex items-center gap-2 group">
                    <Mail size={12} className="text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-700 group-hover:text-blue-600 truncate">{p.email}</span>
                  </a>
                )}
                {p.phone && (
                  <a href={`tel:${p.phone}`} className="flex items-center gap-2 group">
                    <Phone size={12} className="text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-700 group-hover:text-blue-600">{p.phone}</span>
                  </a>
                )}
                {p.linkedin && (
                  <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
                    <Linkedin size={12} className="text-blue-400 flex-shrink-0" />
                    <span className="text-xs text-blue-600 group-hover:underline truncate">{p.linkedin}</span>
                  </a>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  };

  // ── Accès événement (Access field from Sub-Events) ─────────────────────────
  const isEventAccessClosed = eventData?.access === '0';

  // ── Content-display : verrouillage networking ──────────────────────────────
  const isNetworkingLocked = (() => {
    if (!networkingConfig) return false;
    if (networkingConfig.status === 'off') return true;
    const pubDate = networkingConfig.publication_date;
    if (!pubDate) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const pub   = new Date(pubDate); pub.setHours(0, 0, 0, 0);
    return pub > today;
  })();

  // ── Content-display : QrCode badge ────────────────────────────────────────
  const isQrcodeVisible = (() => {
    if (!qrcodeConfig) return false;
    if (qrcodeConfig.status === 'off') return false;
    const pubDate = qrcodeConfig.publication_date;
    if (!pubDate) return true;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const pub   = new Date(pubDate); pub.setHours(0, 0, 0, 0);
    return pub <= today;
  })();

  const isQrcodeLocked = qrcodeConfig && !isQrcodeVisible && qrcodeConfig.status !== 'off';

  const qrUrl = (networkingUser?.record_id && slug)
    ? `https://testing.mobivent.fr/event/${slug}/participants-networking/${networkingUser.record_id}`
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {(eventData?.logo_dark || eventData?.logo) && (
              <img src={eventData.logo_dark || eventData.logo} alt={eventData?.name} className="h-7 object-contain flex-shrink-0" />
            )}
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 leading-tight">{i.networking}</h1>
              {eventData?.name && <p className="text-xs text-gray-400 truncate">{eventData.name}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {networkingUser ? (
              <>
                <button onClick={handleLogout} title={i.logout} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition px-2 py-1 rounded-lg hover:bg-red-50">
                  <LogOut size={14} />
                  <span className="hidden sm:inline">{i.logout}</span>
                </button>
                <button
                  onClick={() => navigate(`/event/${slug}/dashboard?tab=profile`)}
                  className="flex-shrink-0 rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:ring-2 hover:ring-blue-400 hover:ring-offset-1"
                >
                  {myProfile?.photo_thumb || myProfile?.photo ? (
                    <img
                      src={myProfile.photo_thumb || myProfile.photo}
                      alt="Profile"
                      className="w-9 h-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white">
                      <User size={18} />
                    </div>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={isEventAccessClosed ? undefined : () => setLoginModal({ ...MODAL_INIT, open: true })}
                disabled={isEventAccessClosed}
                className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg transition ${
                  isEventAccessClosed
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                }`}
              >
                <LogIn size={14} />
                <span>{i.login_btn}</span>
              </button>
            )}
            <div className="relative">
              <select
                value={lang}
                onChange={e => { setLang(e.target.value); localStorage.setItem('language', e.target.value); }}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                {SUPPORTED_LANGS.map(l => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
              </select>
              <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Tabs (si identifié) */}
        {networkingUser && (
          <div className="flex border-t border-gray-100 max-w-2xl mx-auto">
            <button
              onClick={() => setActiveTab('participants')}
              className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'participants' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Users size={12} className="inline mr-1" />{i.tab_participants}
            </button>
            <button
              onClick={() => setActiveTab('connected')}
              className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'connected' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Check size={12} className="inline mr-1" />{i.tab_connected}
              {connectedList.length > 0 && (
                <span className="ml-1 bg-green-500 text-white rounded-full px-1.5 py-px text-[10px] font-bold">{connectedList.length}</span>
              )}
            </button>
            {qrcodeConfig && qrcodeConfig.status !== 'off' && (
              <button
                onClick={() => setActiveTab('badge')}
                className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'badge' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                <Ticket size={12} className="inline mr-1" />{i.tab_badge}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-4">

        {/* Banner accès événement fermé */}
        {isEventAccessClosed && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
            <p className="text-sm text-amber-800 font-medium">{eventData.access_message || 'Access closed'}</p>
          </div>
        )}

        {/* Banner verrouillé */}
        {isNetworkingLocked && (
          <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white mb-4">
            <div className="absolute inset-0 opacity-5"
              style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
            <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 mb-1">
                  {networkingConfig?.message_title || i.locked_title}
                </p>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  {networkingConfig?.message || i.locked_message}
                </p>
                {networkingConfig?.publication_date && (
                  <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {new Date(networkingConfig.publication_date).toLocaleDateString(
                      lang === 'fr' ? 'fr-FR' : lang === 'de' ? 'de-DE' : 'en-US',
                      { day: 'numeric', month: 'long', year: 'numeric' }
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contenu principal (masqué si verrouillé) */}
        {!isNetworkingLocked && activeTab === 'badge' && networkingUser ? (
          /* ── Onglet Badge QR ── */
          isQrcodeLocked ? (
            <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
              <div className="absolute inset-0 opacity-5"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
              <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                  <QrCode size={28} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 mb-1">
                    {qrcodeConfig?.message_title || i.badge_locked_title}
                  </p>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    {qrcodeConfig?.message || i.badge_locked_message}
                  </p>
                  {qrcodeConfig?.publication_date && (
                    <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {new Date(qrcodeConfig.publication_date).toLocaleDateString(
                        lang === 'fr' ? 'fr-FR' : lang === 'de' ? 'de-DE' : 'en-US',
                        { day: 'numeric', month: 'long', year: 'numeric' }
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : isQrcodeVisible && qrUrl ? (
            <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
              <div className="h-2 w-full" style={{ backgroundColor: eventData?.color_stripe || eventData?.color_main || '#2563EB' }} />
              <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-3">
                <Ticket size={18} style={{ color: eventData?.color_stripe || eventData?.color_main || '#2563EB' }} className="flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 truncate">
                    {myProfile?.firstname} {myProfile?.lastname}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {myProfile?.company || ''}
                    {myProfile?.fonction ? ` · ${myProfile.fonction}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center py-8 gap-3">
                <div className="p-3 bg-white rounded-xl shadow-sm" style={{ border: `2px solid ${(eventData?.color_stripe || eventData?.color_main || '#2563EB')}20` }}>
                  <QRCode
                    value={qrUrl}
                    size={200}
                    level="H"
                    includeMargin={false}
                    renderAs="canvas"
                    fgColor={eventData?.color_stripe || eventData?.color_main || '#2563EB'}
                  />
                </div>
                <p className="text-xs text-gray-400">{i.badge_subtitle}</p>
              </div>
              <div className="px-6 pb-6 flex flex-col gap-3">
                {networkingUser?.record_id && (
                  <button
                    onClick={() => { window.location.href = `/api/generate_wallet.php?record_id=${encodeURIComponent(networkingUser.record_id)}`; }}
                    className="w-full flex items-center justify-center"
                  >
                    <img
                      src="/add-to-wallet.svg"
                      alt={i.badge_add_wallet}
                      className="h-12"
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.parentNode.classList.add('gap-2','px-5','py-2.5','bg-black','text-white','rounded-xl','text-sm','font-medium');
                        e.target.parentNode.insertAdjacentHTML('beforeend',
                          '<span>' + i.badge_add_wallet + '</span>'
                        );
                      }}
                    />
                  </button>
                )}
              </div>
            </div>
          ) : null

        ) : !isNetworkingLocked && (
          <>
            {/* Search */}
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={i.search}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filter/Sort bar */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setFiltersOpen(prev => !prev)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border transition flex-shrink-0 ${filtersOpen || hasFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <Filter size={13} />
                {i.filters}
                {activeFilterCount > 0 && (
                  <span className="bg-blue-600 text-white rounded-full px-1.5 py-px text-[10px] font-bold leading-none">{activeFilterCount}</span>
                )}
                <ChevronDown size={11} className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className="relative flex-1">
                <select
                  value={sortField}
                  onChange={e => setSortField(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-200 rounded-xl pl-3 pr-7 py-2 text-xs font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="default">{i.sort_default}</option>
                  <option value="lastname">{i.sort_lastname}</option>
                  <option value="firstname">{i.sort_firstname}</option>
                  <option value="company">{i.sort_company}</option>
                  <option value="type">{i.sort_type}</option>
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Collapsible filter panel */}
            {filtersOpen && (
              <div className="bg-white border border-gray-100 rounded-xl p-3 mb-3 space-y-3 shadow-sm">
                {allCompanyTypes.length > 0 && (
                  <div>
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{i.filter_company_type}</span>
                    <div className="relative">
                      <select
                        value={selectedCompanyType}
                        onChange={e => setSelectedCompanyType(e.target.value)}
                        className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <option value="">{i.filter_all_types}</option>
                        {allCompanyTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}
                {allCategories.length > 0 && (
                  <div>
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{i.filter_categories}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {allCategories.map(c => (
                        <button key={c} onClick={() => toggleCategory(c)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${selectedCategories.includes(c) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allRequests.length > 0 && (
                  <div>
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{i.filter_requests}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {allRequests.map(r => (
                        <button key={r} onClick={() => toggleRequest(r)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${selectedRequests.includes(r) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {hasFilters && (
                  <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition">
                    <X size={12} />{i.filter_all}
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : error ? (
              <div className="text-center py-16 text-red-500 text-sm">{error}</div>

            ) : activeTab === 'connected' ? (
              connectedList.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Check size={36} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm">{i.no_connected}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {connectedList.map(p => renderCard(p))}
                </div>
              )

            ) : (
              sortedParticipants.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Users size={36} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm">{search || hasFilters ? i.no_results : i.no_participants}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedParticipants.map(p => renderCard(p))}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Login Modal */}
      {loginModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Drag handle — mobile only */}
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="px-6 pt-4 pb-8 sm:p-6" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-gray-900">{i.login_title}</h2>
              <button onClick={() => setLoginModal(MODAL_INIT)} className="text-gray-400 hover:text-gray-600 p-1">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{i.login_subtitle}</p>

            {loginModal.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{loginModal.error}</div>
            )}

            {loginModal.step === 'email' ? (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{i.email_label}</label>
                <div className="relative mb-4">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={loginModal.email}
                    onChange={e => setLoginModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
                    onKeyDown={e => e.key === 'Enter' && handleLoginEmail()}
                    placeholder={i.email_placeholder}
                    className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleLoginEmail}
                  disabled={!loginModal.email || loginModal.loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loginModal.loading ? i.sending : i.send_code}
                </button>
              </>
            ) : (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{i.code_label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={loginModal.code}
                  onChange={e => setLoginModal(prev => ({ ...prev, code: e.target.value.replace(/\D/g, ''), error: '' }))}
                  onKeyDown={e => e.key === 'Enter' && handleLoginCode()}
                  placeholder="123456"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                  autoFocus
                />
                <p className="text-xs text-gray-400 text-center mb-4">{i.code_hint}</p>
                <button
                  onClick={handleLoginCode}
                  disabled={loginModal.code.length !== 6 || loginModal.loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loginModal.loading ? i.verifying : i.verify}
                </button>
                <button
                  onClick={() => setLoginModal(prev => ({ ...prev, step: 'email', code: '', error: '' }))}
                  className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  {i.back}
                </button>
              </>
            )}
            </div>
          </div>
        </div>
      )}
      {/* ====== POPUP RATE LIMIT NETWORKING ====== */}
      {rateLimitModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⏱</span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">{i.rate_limit_title}</h2>
            <p className="text-sm text-gray-600 mb-4">{i.rate_limit_msg}</p>
            {rateLimitCountdown > 0 && (
              <div className="mb-5">
                <p className="text-xs text-gray-500 mb-1">{i.rate_limit_hint}</p>
                <div className="text-3xl font-mono font-bold text-orange-500">
                  {String(Math.floor(rateLimitCountdown / 60)).padStart(2, '0')}:{String(rateLimitCountdown % 60).padStart(2, '0')}
                </div>
              </div>
            )}
            <button
              onClick={() => setRateLimitModal({ open: false, retryAfter: 0 })}
              className="w-full bg-gray-900 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-800 transition"
            >
              {i.rate_limit_ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkingParticipantsPage;
